'use client';
import React, { useState } from 'react';

export default function DownloadCTA() {
  const [platform, setPlatform] = useState<'ios' | 'android' | null>(null);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (platform && email) {
      setSubmitted(true);
    }
  };

  return (
    <section id="download" className="py-24 px-6 max-w-7xl mx-auto">
      <div className="max-w-3xl mx-auto">
        {/* Outer container */}
        <div className="rounded-3xl overflow-hidden relative"
          style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #0F0E1A 50%, #16132A 100%)', border: '1px solid rgba(124,58,237,0.35)' }}>
          {/* Ambient glow top */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse, rgba(124,58,237,0.3) 0%, transparent 70%)', filter: 'blur(20px)' }} />

          <div className="relative z-10 p-10 md:p-14">
            {/* Label */}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-6 font-mono text-xs"
              style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)', color: '#C4B5FD' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
              Download Reader
            </div>

            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3" style={{ color: '#F4F4F5' }}>
              Read everything. Anywhere.
            </h2>
            <p className="text-sm leading-relaxed mb-10" style={{ color: '#71717A' }}>
              The Basecamp Reader gives you offline access, code syntax highlighting, forked base downloads,
              and push notifications when a series you follow publishes a new part.
            </p>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Platform toggle — first */}
                <div>
                  <label className="block font-mono text-xs mb-3 tracking-wider uppercase" style={{ color: '#52525B' }}>
                    01. Choose your platform
                  </label>
                  <div className="flex gap-3">
                    {(['ios', 'android'] as const).map(p => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setPlatform(p)}
                        className={`platform-btn flex-1 py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 ${platform === p ? 'selected' : ''}`}
                        style={{
                          background: platform === p ? '#7C3AED' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${platform === p ? '#7C3AED' : 'rgba(255,255,255,0.1)'}`,
                          color: platform === p ? 'white' : '#71717A',
                        }}
                      >
                        {p === 'ios' ? (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                            </svg>
                            iOS
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.523 15.341a.755.755 0 0 1-.752.752H7.229a.755.755 0 0 1-.752-.752V8.297c0-.415.337-.752.752-.752h9.542c.415 0 .752.337.752.752v7.044zM7.5 5.25L5.25 3M16.5 5.25L18.75 3M8.25 20.25h7.5"/>
                            </svg>
                            Android
                          </>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Email — second */}
                <div>
                  <label className="block font-mono text-xs mb-3 tracking-wider uppercase" style={{ color: '#52525B' }}>
                    02. Your email
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="flex-1 px-4 py-3 rounded-xl text-sm font-mono outline-none transition-all duration-200"
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(124,58,237,0.2)',
                        color: '#F4F4F5',
                      }}
                      onFocus={e => (e.target as HTMLElement).style.borderColor = 'rgba(124,58,237,0.6)'}
                      onBlur={e => (e.target as HTMLElement).style.borderColor = 'rgba(124,58,237,0.2)'}
                      required
                    />
                    <button
                      type="submit"
                      disabled={!platform || !email}
                      className="px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{
                        background: platform && email ? '#7C3AED' : 'rgba(124,58,237,0.3)',
                        color: 'white',
                        boxShadow: platform && email ? '0 0 20px rgba(124,58,237,0.4)' : 'none',
                      }}
                    >
                      Get link →
                    </button>
                  </div>
                </div>

                <p className="font-mono text-xs" style={{ color: '#3F3F46' }}>
                  No account required. Download link sent instantly. Unsubscribe anytime.
                </p>
              </form>
            ) : (
              <div className="text-center py-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ background: 'rgba(124,58,237,0.2)', border: '1px solid rgba(124,58,237,0.4)' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C4B5FD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ color: '#F4F4F5' }}>Check your inbox</h3>
                <p className="text-sm" style={{ color: '#71717A' }}>
                  Download link sent to <span style={{ color: '#C4B5FD' }}>{email}</span> for {platform === 'ios' ? 'iOS' : 'Android'}.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          {[
            { value: '12,400+', label: 'Active readers' },
            { value: '340+', label: 'Articles indexed' },
            { value: '4.8★', label: 'App Store rating' },
          ].map(stat => (
            <div key={stat.label} className="text-center py-4 px-3 rounded-xl"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(124,58,237,0.1)' }}>
              <div className="text-xl font-bold mb-1" style={{ color: '#C4B5FD' }}>{stat.value}</div>
              <div className="font-mono text-xs" style={{ color: '#52525B' }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}